import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FileText, Send, Printer, Sparkles, Mail, RefreshCw, Plus, Trash2, Eye, Receipt, Pencil } from 'lucide-react';
import { isoDate } from '../../utils/time';
import { useConfirm } from '../../components/ConfirmDialog';
import StatementLetters from './StatementLetters';
import {
  PageHead, Panel, Register, RailList, RailRow, FigureStrip, Figure, DetailDrawer, DataTable,
  AcStatus, Category, Currency, Deadline, fmtDate, money, monthLabel, normaliseCategory,
} from './design';

// monthLabel (from ./design): the month a statement covers, written the way a person says it:
// "June 2026".

// The statutory deadline: the last day of the month after the one the statement covers. Mirrors
// statementDueDate() on the server so a row with no server-supplied due_date still shows the real
// obligation rather than a blank.
const dueFor = (period) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) + 1, 0)).toISOString().slice(0, 10);
};

const schemeLabel = (scheme) => (String(scheme || '').toUpperCase() === 'HCP' ? 'Home Care Package' : 'Support at Home');

// Production carries 'Emailed', 'Printed', 'Posted' and a legacy lowercase 'sent' for the same fact.
const isSent = (r) => (r.sent != null
  ? !!r.sent
  : ['emailed', 'printed', 'sent', 'posted'].includes(String(r.status || '').trim().toLowerCase()));

// One sentence per month that is still owed, so the operator knows which of the two jobs is left.
const owedDetail = (m) => {
  const p = Number(m.participants || 0);
  const ng = Number(m.not_generated || 0);
  const ns = Number(m.not_sent || 0);
  const noStatement = `${ng} of ${p} participant${p === 1 ? '' : 's'} ${ng === 1 ? 'has' : 'have'} no statement yet`;
  if (ng > 0 && ns > 0) return `${noStatement}, and ${ns} more ${ns === 1 ? 'is' : 'are'} generated but not sent.`;
  if (ng > 0) return `${noStatement}.`;
  return `${ns} statement${ns === 1 ? '' : 's'} generated but not sent.`;
};

const failureReason = (f) => {
  if (f.reason === 'no_email' || f.reason === 'no_recipient') return 'No email or postal address on file, and no supporter set to receive it. Add one on their funding plan.';
  if (f.reason === 'estimates') return 'Held: some amounts are estimates until Services Australia pays this month.';
  if (f.reason === 'incomplete') return `Not complete: ${(f.problems?.errors || []).join('; ')}`;
  if (f.reason === 'not_found') return 'The statement could not be found.';
  return f.detail || f.reason || 'The mail server rejected it.';
};

// A load that failed says so, briefly, with a way to try again. Inside the drawer the full-size
// ErrorState would push the statement itself out of view; saying nothing reads as "not sent".
function LoadError({ message, onRetry }) {
  return (
    <p role="alert" className="flex flex-wrap items-center gap-2 text-[13px] text-red-800">
      {message}
      <button type="button" onClick={onRetry} className="btn-ghost btn-sm">Try again</button>
    </p>
  );
}

// Who the statement went to and how, and anything that makes it incomplete.
function SentTo({ statementId }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    api.get(`/aged-care/statements/${statementId}/deliveries`)
      .then((r) => setD(r.data))
      .catch((e) => { setD(null); setError(e.response?.data?.message || 'Could not load who this statement was sent to'); });
  }, [statementId]);
  useEffect(() => { load(); }, [load]);
  if (error) return <Panel title="Sent to" bodyClass="px-4 py-3"><LoadError message={error} onRetry={load} /></Panel>;
  if (!d) return null;
  const errs = d.problems?.errors || []; const warns = d.problems?.warnings || [];
  const deliveries = d.deliveries || [];
  const STATUS = { sent: 'emailed', failed: 'email failed', queued: 'letter to post', posted: 'posted' };
  return (
    <Panel title="Sent to" bodyClass="px-4 py-3 text-[13px] space-y-1">
      {errs.map((e, i) => <p key={`e${i}`} className="text-red-800">Not complete: {e}</p>)}
      {warns.map((w, i) => <p key={`w${i}`} className="text-amber-800">{w}</p>)}
      {!deliveries.length && <p className="text-slate-500">Not sent yet. It goes to the participant and any supporter with consent to share, by email or letter as each prefers.</p>}
      {deliveries.map((x) => (
        <p key={x.id} className={x.status === 'failed' ? 'text-red-800' : 'text-slate-700'}>
          {x.name}{x.recipient_kind === 'supporter' ? ' (supporter)' : ''}: {STATUS[x.status] || x.status} {x.method === 'email' ? `to ${x.sent_to}` : ''}
          <span className="text-slate-500"> · {fmtDate(x.posted_on || x.at)}{x.by_name ? ` · ${x.by_name}` : ''}{x.detail ? ` · ${x.detail}` : ''}</span>
        </p>
      ))}
    </Panel>
  );
}

// Personal care is free to the participant from 1 October 2026, so the server needs to know which
// lines are personal care to work out the contribution.
const PERSONAL_CARE = 'Personal care (free from 1 October 2026)';

// The fields of one statement line, for adding a line and for changing one added by hand, so the
// two forms can never ask for different things. Two columns on a phone (service and category full
// width), a twelve-column line on anything wider. `children` are the form's buttons.
function LineFields({ value, onChange, autoFocus = false, children }) {
  const input = 'input-field !rounded-lg !px-2 !py-1.5 !text-xs';
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange((f) => ({ ...f, [k]: v }));
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center">
      <input type="date" aria-label="Service date" value={value.service_date} onChange={set('service_date')} className={`col-span-2 ${input}`} />
      <input placeholder="Service" aria-label="Service" autoFocus={autoFocus} value={value.description} onChange={set('description')} className={`col-span-2 sm:col-span-4 ${input}`} />
      <select aria-label="Category" value={value.category} onChange={set('category')} className={`col-span-2 ${input}`}>
        <option value="clinical">Clinical</option>
        <option value="independence">Independence</option>
        <option value="everyday">Everyday living</option>
      </select>
      <input type="number" min="0" step="0.5" placeholder="Units" aria-label="Units" value={value.units} onChange={set('units')} className={`col-span-1 sm:col-span-2 ${input}`} />
      <input type="number" min="0" step="0.01" placeholder="Rate" aria-label="Rate" value={value.rate} onChange={set('rate')} className={`col-span-1 sm:col-span-2 ${input}`} />
      <label className="col-span-2 sm:col-span-7 flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
        <input type="checkbox" checked={!!value.isPersonalCare} onChange={set('isPersonalCare')} /> {PERSONAL_CARE}
      </label>
      <div className="col-span-2 sm:col-span-5 flex flex-wrap items-center justify-end gap-2">{children}</div>
    </div>
  );
}

function StatementDrawer({ row, onClose, onSend, onChanged }) {
  const id = row.id;
  const [s, setS] = useState(row);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ service_date: isoDate(), description: '', category: 'independence', units: 1, rate: '', isPersonalCare: false });
  const confirm = useConfirm();

  // A failed load keeps the row from the list for the summary, but says the lines could not be
  // loaded rather than showing "no services", which would read as an empty statement.
  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try { const r = await api.get(`/aged-care/statements/${id}`); setS(r.data); setLines(r.data.lines || []); }
    catch (e) { setLoadError(e.response?.data?.message || 'Could not load this statement'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const addLine = async () => {
    if (!form.description.trim() || form.rate === '') return toast.error('A service description and a rate are required');
    setAdding(true);
    try {
      await api.post(`/aged-care/statements/${id}/lines`, { service_date: form.service_date, description: form.description.trim(), category: form.category, units: Number(form.units) || 0, rate: Number(form.rate) || 0, isPersonalCare: !!form.isPersonalCare });
      toast.success('Line added');
      setForm(f => ({ ...f, description: '', rate: '', isPersonalCare: false }));
      await load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not add the line'); } finally { setAdding(false); }
  };

  // A line already in a claim is corrected, not deleted: the correction is listed under Claims
  // for the Provider Portal.
  const [amending, setAmending] = useState(null);   // { id, units, rate, reason }
  const claimed = (l) => !!(l.sa_ext_ref || l.sa_item_id || l.sa_paid_date);

  // A line added by hand and not yet claimed can be changed in place. A line from a visit follows
  // the visit (the server refuses to change it here), and a claimed line is corrected instead.
  const [editing, setEditing] = useState(null);     // { id, service_date, description, category, units, rate, isPersonalCare }
  const [savingLine, setSavingLine] = useState(false);
  const startEdit = (l) => {
    setAmending(null);
    setEditing({
      id: l.id,
      service_date: String(l.service_date || '').slice(0, 10) || isoDate(),
      description: l.description || '',
      category: normaliseCategory(l.category) || 'independence',
      units: String(Number(l.units) || 0),
      rate: String(Number(l.rate) || 0),
      isPersonalCare: !!l.is_personal_care,
    });
  };
  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.description.trim() || editing.rate === '') return toast.error('A service description and a rate are required');
    setSavingLine(true);
    try {
      await api.put(`/aged-care/statements/${id}/lines/${editing.id}`, {
        service_date: editing.service_date, description: editing.description.trim(), category: editing.category,
        units: Number(editing.units) || 0, rate: Number(editing.rate) || 0, isPersonalCare: !!editing.isPersonalCare,
      });
      toast.success('Line saved');
      setEditing(null); await load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the line'); } finally { setSavingLine(false); }
  };

  const delLine = async (l) => {
    const what = l.description ? `"${l.description}"` : 'This line';
    const ok = await confirm(
      `${what} on ${fmtDate(l.service_date)} comes off ${s.client_name ? `${s.client_name}'s` : 'this'} statement, and the balance is worked out again.${l.shift_id ? ' The visit itself stays on the scheduler.' : ''}`,
      { title: 'Remove this line?', confirmLabel: 'Remove line', danger: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/aged-care/statements/${id}/lines/${l.id}`);
      toast.success('Line removed');
      if (editing && editing.id === l.id) setEditing(null);
      await load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not remove the line'); }
  };
  const saveAmend = async () => {
    if (!amending) return;
    if (!amending.reason.trim()) return toast.error('Say why the line is being corrected');
    try {
      await api.post(`/aged-care/statements/${id}/lines/${amending.id}/amend`, { units: Number(amending.units) || 0, rate: Number(amending.rate) || 0, reason: amending.reason });
      toast.success('Corrected. The change to make in the Provider Portal is listed under Claims.');
      setAmending(null); await load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record the correction'); }
  };
  const printStmt = async () => {
    try { const r = await api.get(`/aged-care/statements/${id}/html`); const w = window.open('', '_blank'); if (w) { w.document.write(typeof r.data === 'string' ? r.data : ''); w.document.close(); } }
    catch { toast.error('Could not open the statement'); }
  };
  // The participant is entitled to a record they can keep, not just a page in a browser.
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/aged-care/statements/${s.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `support-at-home-statement-${s.period || ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not download the statement PDF'); }
  };

  const Line = ({ label, value, sign, strong, muted }) => (
    <div className={`flex items-center justify-between py-1.5 ${strong ? 'border-t border-slate-200 mt-1 pt-2.5' : ''}`}>
      <span className={`text-[13px] ${strong ? 'font-semibold text-slate-800' : muted ? 'text-slate-500' : 'text-slate-600'}`}>
        {sign && <span className="inline-block w-4 text-slate-500">{sign}</span>}{label}
      </span>
      <span className={`text-[13px] ${strong ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
        {sign === '−' && <span className="text-slate-500">-</span>}<Currency value={value} />
      </span>
    </div>
  );

  const lineCols = [
    { key: 'service_date', header: 'Date', width: 96, render: (l) => fmtDate(l.service_date) },
    { key: 'description', header: 'Service', render: (l) => l.description || '—' },
    { key: 'category', header: 'Category', render: (l) => <Category value={l.category} /> },
    { key: 'units', header: 'Units', align: 'right', render: (l) => Number(l.units) || 0 },
    { key: 'rate', header: 'Rate', money: true },
    { key: 'cost', header: 'Cost', money: true },
    { key: 'client_contribution', header: 'Participant', money: true },
    { key: 'gov_subsidy', header: 'Government', money: true },
    { key: 'evidence', header: '', width: 84, render: (l) => (l.shift_id ? (
      <button type="button" className="btn-ghost btn-sm" title="Everything that shows this visit happened: clock-in and out, location, notes, the claim"
        onClick={async () => {
          try {
            const r = await api.get(`/aged-care/evidence/visit/${l.shift_id}`, { params: { client_id: s.client_id, format: 'pdf' }, responseType: 'blob' });
            const url = window.URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `visit-evidence-${String(l.service_date || '').slice(0, 10)}.pdf`; a.click(); window.URL.revokeObjectURL(url);
          } catch { toast.error('Could not gather the evidence'); }
        }}>Evidence</button>
    ) : null) },
  ];

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={s.client_name || 'Statement'}
      icon={FileText}
      subtitle={`${schemeLabel(s.scheme)}${s.classification ? `, ${s.classification}` : ''}`}
      statusNode={<AcStatus status={s.status || 'Draft'} />}
      meta={[
        { label: 'Period', value: monthLabel(s.period) },
        { label: 'Due', value: <Deadline date={s.due_date || dueFor(s.period)} done={isSent(s)} /> },
        { label: 'Closing balance', value: money(s.closing), money: true },
      ]}
      width="xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-ghost">Close</button>
          <button type="button" onClick={printStmt} className="btn-secondary"><Printer size={15} /> Print</button>
          <button type="button" onClick={downloadPdf} className="btn-secondary"><FileText size={15} /> Download PDF</button>
          <button type="button" onClick={() => onSend(s)} className="btn-primary"><Mail size={15} /> Send statement</button>
        </>
      )}
    >
      <div className="space-y-4">
        {amending && (
          <Panel tone="muted" bodyClass="px-4 py-3 text-[13px] text-slate-700 space-y-2">
            <p>Correct a line that is already in a claim. Set the quantity or price to 0 to reverse it. The participant's contribution changes in proportion, and the correction to make in the Provider Portal is listed under Claims.</p>
            <div className="flex flex-wrap items-center gap-2">
              <input aria-label="Quantity" type="number" step="0.25" min="0" className="input-field !w-24 !py-1.5 !text-[13px]" value={amending.units} onChange={(e) => setAmending((a) => ({ ...a, units: e.target.value }))} />
              <span>×</span>
              <input aria-label="Price per unit" type="number" step="0.01" min="0" className="input-field !w-28 !py-1.5 !text-[13px]" value={amending.rate} onChange={(e) => setAmending((a) => ({ ...a, rate: e.target.value }))} />
              <input aria-label="Why" className="input-field !w-72 !py-1.5 !text-[13px]" placeholder="Why, for example: visit was 1.5 hours, not 2" value={amending.reason} onChange={(e) => setAmending((a) => ({ ...a, reason: e.target.value }))} />
              <button type="button" onClick={saveAmend} className="btn-primary btn-sm">Save correction</button>
              <button type="button" onClick={() => setAmending(null)} className="btn-ghost btn-sm">Cancel</button>
            </div>
          </Panel>
        )}
        <SentTo statementId={id} />
        <Panel title="Services delivered">
          <DataTable
            columns={lineCols} rows={lines} rowKey="id" loading={loading} dense stickyHeader={false}
            error={loadError} onRetry={load}
            label="Services delivered"
            rowActions={(l) => (claimed(l) ? (
              <span className="flex items-center gap-1.5">
                <span className={`badge ${l.sa_paid_date ? 'badge-green' : 'badge-blue'}`}>{l.sa_paid_date ? 'Paid' : 'Claimed'}</span>
                <button type="button" onClick={() => { setEditing(null); setAmending({ id: l.id, units: String(Number(l.units) || 0), rate: String(Number(l.rate) || 0), reason: '' }); }} className="btn-ghost btn-sm">Correct</button>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                {l.shift_id ? (
                  // Changed on the scheduler, never here: the line is rebuilt from the visit.
                  <span className="text-[11px] text-slate-500 whitespace-nowrap" title="This line follows the visit. Change the visit on the scheduler.">From a visit</span>
                ) : (
                  <button type="button" onClick={() => startEdit(l)} className="btn-ghost btn-sm" aria-label="Edit this line"><Pencil size={13} /> Edit</button>
                )}
                <button type="button" onClick={() => delLine(l)} className="btn-ghost btn-sm" aria-label="Remove this line" title="Remove this line">
                  <Trash2 size={14} />
                </button>
              </span>
            ))}
            empty={{
              icon: FileText,
              title: 'No services on this statement yet',
              hint: 'Add a line below, or record shifts for the month and generate statements again.',
            }}
          />
          {/* One form at a time: changing a line takes the place of adding one. It is not offered
              while the lines failed to load, since what it would change cannot be seen. */}
          {!loadError && (
            <div className="p-3 bg-slate-50/60 border-t border-slate-100 space-y-2">
              {editing ? (
                <>
                  <p className="text-[12px] font-medium text-slate-700">Change this line</p>
                  <LineFields key={`edit-${editing.id}`} value={editing} onChange={setEditing} autoFocus>
                    <button type="button" onClick={() => setEditing(null)} className="btn-ghost btn-sm">Cancel</button>
                    <button type="button" onClick={saveEdit} disabled={savingLine} className="btn-primary btn-sm">{savingLine ? 'Saving' : 'Save line'}</button>
                  </LineFields>
                </>
              ) : (
                <LineFields key="add" value={form} onChange={setForm}>
                  <button type="button" onClick={addLine} disabled={adding} className="btn-secondary btn-sm justify-center"><Plus size={13} /> Add line</button>
                </LineFields>
              )}
            </div>
          )}
        </Panel>

        <Panel title="Statement summary" bodyClass="px-4 py-2">
          <Line label="Opening balance" value={s.opening} />
          <Line label="Commonwealth subsidy" value={s.subsidy} sign="+" />
          <Line label="Supplements" value={s.supplements} sign="+" />
          <Line label={String(s.scheme || '').toUpperCase() === 'HCP' ? 'Care and package management fees' : 'Care management, 10% of the budget'} value={s.fees} sign="−" />
          <Line label="Services delivered, full price" value={s.expenditure} sign="−" />
          <Line label="Closing balance" value={s.closing} strong />
          <p className="text-[12px] text-slate-500 pt-2">
            Participant contributions of {money(s.contributions)} are part of the service price and invoiced to them separately.
            Care management lines are paid from your care management account, not this budget.
          </p>
        </Panel>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Panel tone="muted" bodyClass="px-4 py-3">
            <Figure label="Support at Home funds, held by Services Australia" value={money(s.held_government)} />
          </Panel>
          {(Number(s.hcp_unspent_provider || 0) + Number(s.hcp_unspent_government || 0)) > 0 && (
            <Panel tone="muted" bodyClass="px-4 py-3">
              <Figure label="Unspent HCP funds, held by you / Services Australia" value={`${money(s.hcp_unspent_provider)} / ${money(s.hcp_unspent_government)}`} />
            </Panel>
          )}
        </div>
      </div>
    </DetailDrawer>
  );
}

export default function AgedCareStatements({ clientId: only = '', embedded = false } = {}) {
  // Statements are issued in arrears, so the month just finished is the one to work on.
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const defaultPeriod = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
  const [loadError, setLoadError] = useState(null);

  const [period, setPeriod] = useState(defaultPeriod);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [outstanding, setOutstanding] = useState(null); // null until the first answer arrives
  const [outstandingError, setOutstandingError] = useState(null);
  const [failures, setFailures] = useState(null);       // what the last send could not send
  const [lettersKey, setLettersKey] = useState(0);       // reloads the letters to post
  const confirm = useConfirm();
  // Stable, or the drawer re-runs its focus effect whenever this list re-renders (after a line is
  // added, for one) and pulls focus out of the drawer.
  const closeView = useCallback(() => setViewRow(null), []);

  const load = async (p = period) => {
    setLoading(true); setLoadError(null);
    try {
      // Inside a participant's file: all their months. Otherwise one month for everyone.
      const res = await api.get('/aged-care/statements', { params: only ? { client_id: only } : (p ? { period: p } : {}) });
      const data = Array.isArray(res.data) ? res.data : (res.data?.statements || []);
      setRows(data);
    } catch (e) {
      // Never an empty list on a failed load: "Generate statements" would then overwrite them.
      setRows([]);
      setLoadError(e.response?.data?.message || 'Could not load the statements');
    } finally {
      setLoading(false);
    }
  };

  // Which recent months are not yet issued to every participant. A nil month still owes a statement.
  // A failed check is said as such: an empty answer would print "every participant has been issued
  // a statement", which is the one thing this screen must never say without knowing it.
  const loadOutstanding = async () => {
    setOutstandingError(null);
    try {
      const r = await api.get('/aged-care/statements/outstanding');
      setOutstanding(Array.isArray(r.data?.months) ? r.data.months : []);
    } catch (e) {
      setOutstanding(null);
      setOutstandingError(e.response?.data?.message || 'Could not check which months are still owed');
    }
  };

  useEffect(() => { load(period); }, [period]);
  useEffect(() => { if (!embedded) loadOutstanding(); }, [embedded]);

  // Only what the server returned. On a screen whose whole job is proving statements were issued,
  // a made-up row marked Emailed would read as evidence.
  const display = rows;

  const totals = display.reduce((a, r) => ({
    closing: a.closing + Number(r.closing || 0),
    subsidy: a.subsidy + Number(r.subsidy || 0),
    expenditure: a.expenditure + Number(r.expenditure || 0),
  }), { closing: 0, subsidy: 0, expenditure: 0 });

  const sent = display.filter(r => (r.status || 'Draft') !== 'Draft').length;

  const generate = async () => {
    if (!period) return toast.error('Choose a month first');
    const ok = await confirm(
      `Each participant's statement for ${monthLabel(period)} is worked out again from its lines, and anyone without one gets one, including participants with no services that month.`,
      { title: `Generate statements for ${monthLabel(period)}?`, confirmLabel: 'Generate statements' },
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post('/aged-care/statements/generate', { period });
      const skipped = Number(r.data?.skipped || 0);
      toast.success(skipped
        ? `Statements generated. ${skipped} participant${skipped === 1 ? '' : 's'} skipped: their plan starts after this month.`
        : 'Statements generated');
      await load(period);
      await loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not generate statements');
    } finally {
      setBusy(false);
    }
  };

  // The participant is invoiced their own share directly (Services Australia pays the rest). This
  // drafts one invoice per participant for the month. After a payment statement is imported in
  // Claims it happens by itself from the confirmed amounts; before then the amounts come from the
  // participant's contribution notice and are marked as estimates.
  const contributionInvoices = async () => {
    if (!period) return toast.error('Choose a month first');
    const ok = await confirm(
      `A draft invoice for their own contribution is made for each participant for ${monthLabel(period)}, or updated where one exists. Until the payment statement is imported, the amounts are estimates from each participant's contribution notice.`,
      { title: `Draft contribution invoices for ${monthLabel(period)}?`, confirmLabel: 'Draft invoices' },
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post('/aged-care/statements/contribution-invoices', { period });
      const made = (data.created || []).length; const redone = (data.updated || []).length;
      if (!made && !redone) toast('No participant has a contribution to invoice for this month.');
      else toast.success(`${made} contribution invoice${made === 1 ? '' : 's'} drafted${redone ? `, ${redone} updated` : ''}. They are in Invoices under "Aged care contribution".`);
      for (const x of (data.skipped || [])) toast(`${x.participant}: not invoiced (${x.reason})`, { icon: '⚠️' });
      for (const x of (data.adjustments || [])) toast(`${x.participant}: ${x.invoice} was sent for ${money(x.was)}; the contribution is now ${money(x.now)}.`, { icon: '⚠️', duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not create the contribution invoices');
    } finally { setBusy(false); }
  };

  // Send the month: each statement to the participant and consenting supporters, by email or
  // letter as each prefers. Statements already sent are not sent again.
  const dispatch = async (allowEstimates = false) => {
    if (!period) return toast.error('Choose a month first');
    const month = monthLabel(period);
    const held = failures?.estimates || 0;
    const unsent = rows.filter((r) => !isSent(r)).length;
    const ok = await confirm(
      allowEstimates
        ? `${held || 'Some'} ${month} statement${held === 1 ? '' : 's'} ${held === 1 ? 'has' : 'have'} amounts that stay estimates until Services Australia pays this month. ${held === 1 ? 'It is' : 'They are'} sent now as ${held === 1 ? 'it stands' : 'they stand'}.`
        : `Each ${month} statement not sent yet${unsent ? ` (${unsent})` : ''} goes to the participant and any supporter with consent to share, by email or as a letter to post, as each prefers. Statements already sent are not sent again.`,
      allowEstimates
        ? { title: 'Send with estimates?', confirmLabel: 'Send them' }
        : { title: `Send the ${month} statements?`, confirmLabel: 'Send statements' },
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post('/aged-care/statements/dispatch', { period, allowEstimates });
      const d = r.data || {};
      const fails = Array.isArray(d.failures) ? d.failures : [];
      setFailures(fails.length ? { emailed: Number(d.emailed || 0), letters: Number(d.letters || 0), estimates: Number(d.estimates || 0), failures: fails } : null);
      if (fails.length) toast(d.message); else toast.success(d.message || 'Statements sent');
      setLettersKey((k) => k + 1);
      await load(period);
      await loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not send the statements');
    } finally {
      setBusy(false);
    }
  };

  const sendOne = async (row) => {
    const send =(allowEstimates) => api.post(`/aged-care/statements/${row.id}/send`, { allowEstimates });
    try {
      let r;
      try { r = await send(false); } catch (e) {
        if (e.response?.data?.code !== 'ESTIMATES') throw e;
        const ok = await confirm(e.response.data.message, { title: 'Send with estimates?', confirmLabel: 'Send it' });
        if (!ok) return;
        r = await send(true);
      }
      const res = r.data?.results || [];
      const emailedTo = res.filter((x) => x.status === 'sent').map((x) => x.name);
      const letters = res.filter((x) => x.status === 'queued').length;
      toast.success([emailedTo.length ? `Emailed to ${emailedTo.join(', ')}` : null, letters ? `${letters} letter${letters === 1 ? '' : 's'} to post` : null].filter(Boolean).join('. ') || 'Sent');
      setViewRow(null); setLettersKey((k) => k + 1);
      await load(period);
      await loadOutstanding();
    } catch (e) {
      // NO_RECIPIENT and INCOMPLETE name the participant and say what to do; a generic line would hide that.
      toast.error(e.response?.data?.message || 'Could not send the statement');
    }
  };

  const owed = (outstanding || []).filter((m) => Number(m.not_generated || 0) > 0 || Number(m.not_sent || 0) > 0);

  const columns = [
    {
      key: 'client_name', header: 'Participant', sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-800 truncate">{r.client_name || '—'}</div>
          <div className="text-[11px] text-slate-500 truncate">{schemeLabel(r.scheme)}{r.classification ? `, ${r.classification}` : ''}</div>
        </div>
      ),
    },
    {
      key: 'due_date', header: 'Due', sortable: true,
      sortValue: (r) => r.due_date || dueFor(r.period),
      render: (r) => <Deadline date={r.due_date || dueFor(r.period)} done={isSent(r)} />,
    },
    { key: 'opening', header: 'Opening', money: true },
    { key: 'subsidy', header: 'Subsidy', money: true },
    { key: 'contributions', header: 'Contributions', money: true },
    { key: 'expenditure', header: 'Expenditure', money: true },
    {
      key: 'closing', header: 'Closing', money: true, sortable: true,
      render: (r) => <Currency value={r.closing} className="font-medium text-slate-900" />,
    },
    { key: 'status', header: 'Status', render: (r) => <AcStatus status={r.status || 'Draft'} /> },
  ];

  return (
    <div>
      {!embedded && <>
      <PageHead icon={FileText}
        title="Statements"
        purpose="Every participant is owed a statement for every month, including months with no services, by the last day of the following month."
        actions={(
          <>
            <button type="button" onClick={() => { load(period); loadOutstanding(); }} className="btn-ghost"><RefreshCw size={15} /> Refresh</button>
            <button type="button" onClick={generate} disabled={busy || !!loadError} className="btn-primary"><Sparkles size={15} /> Generate statements</button>
          </>
        )}
      />

      {owed.length > 0 && (
        <section className="mb-5" aria-label="Months still owed">
          <h3 className="text-[13px] font-semibold text-slate-800 mb-2">Months still owed to participants</h3>
          <RailList>
            {owed.map((m) => (
              <RailRow
                key={m.period}
                severity={m.overdue ? 'overdue' : 'soon'}
                title={monthLabel(m.period)}
                detail={owedDetail(m)}
                right={<Deadline date={m.due_date} />}
                onClick={() => setPeriod(m.period)}
              />
            ))}
          </RailList>
        </section>
      )}
      {outstanding && owed.length === 0 && (
        <p className="text-[13px] text-slate-500 mb-5">Statements for the last six months have been issued to every participant.</p>
      )}
      {outstandingError && (
        <div className="mb-5"><LoadError message={outstandingError} onRetry={loadOutstanding} /></div>
      )}

      {failures && (
        <Panel
          title="Statements not sent yet"
          actions={<button type="button" onClick={() => setFailures(null)} className="btn-ghost btn-sm">Dismiss</button>}
          className="mb-5"
          bodyClass="px-4 py-3"
        >
          <p className="text-[13px] text-slate-600 mb-2">
            {failures.emailed} statement{failures.emailed === 1 ? '' : 's'} emailed{failures.letters ? `, ${failures.letters} with a letter to post` : ''}. {failures.failures.length} not sent yet.
            {failures.estimates > 0 && <button type="button" className="btn-secondary btn-sm ml-2" disabled={busy} onClick={() => dispatch(true)}>Send the {failures.estimates} with estimates</button>}
          </p>
          <div className="divide-y divide-slate-100">
            {failures.failures.map((f, i) => (
              <div key={i} className="py-2 flex items-baseline justify-between gap-4 text-[13px]">
                <span className="font-medium text-slate-800">{f.client || 'A participant'}</span>
                <span className="text-slate-500 text-right">{failureReason(f)}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <StatementLetters period={period} refreshKey={lettersKey} onChanged={() => { load(period); loadOutstanding(); }} />

      <FigureStrip>
        <Figure label="Statements" value={display.length} sub={monthLabel(period)} />
        <Figure label="Sent" value={`${sent} of ${display.length}`} />
        <Figure label="Subsidy this month" value={money(totals.subsidy)} />
        <Figure label="Closing balances" value={money(totals.closing)} />
      </FigureStrip>
      </>}

      <Register
        title={embedded ? 'Monthly statements' : `Statements for ${monthLabel(period)}`}
        count={display.length}
        error={loadError} onRetry={() => load(period)}
        actions={embedded ? null : (
          <>
            <input
              type="month"
              aria-label="Statement month"
              value={period}
              onChange={e => setPeriod(e.target.value)}
              className="input-field !w-auto !rounded-lg !py-1.5 !text-[13px]"
            />
            <button type="button" onClick={contributionInvoices} disabled={busy} className="btn-secondary btn-sm" title="One invoice per participant for their contribution this month"><Receipt size={14} /> Contribution invoices</button>
            <button type="button" onClick={() => dispatch(false)} disabled={busy} className="btn-secondary btn-sm"><Send size={14} /> Send all</button>
          </>
        )}
        columns={embedded
          ? [{ key: 'period', header: 'Month', sortable: true, render: (r) => <span className="font-medium text-slate-800">{monthLabel(r.period)}</span> }, ...columns.slice(1)]
          : columns}
        rows={embedded ? [...display].sort((a, b) => String(b.period).localeCompare(String(a.period))) : display}
        rowKey="id"
        loading={loading}
        onRowClick={(r) => setViewRow(r)}
        activeRowKey={viewRow ? viewRow.id : null}
        rowTone={(r) => (r.overdue ? 'rose' : null)}
        rowActions={(r) => (
          <>
            <button type="button" onClick={(e) => { e.stopPropagation(); setViewRow(r); }} className="btn-ghost btn-sm"><Eye size={13} /> View</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); sendOne(r); }} className="btn-secondary btn-sm"><Mail size={13} /> Send</button>
          </>
        )}
        empty={{
          icon: FileText,
          title: `No statements for ${monthLabel(period)}`,
          hint: 'Generate statements to create a balance for every participant, including those with no services this month.',
          action: <button type="button" onClick={generate} disabled={busy} className="btn-primary btn-sm"><Sparkles size={14} /> Generate statements</button>,
        }}
        footer={display.length
          ? `Total expenditure ${money(totals.expenditure)}. Closing balances combine funds held by Services Australia and by the provider.`
          : null}
      />

      {viewRow && <StatementDrawer row={viewRow} onClose={closeView} onSend={sendOne} onChanged={() => load(period)} />}
    </div>
  );
}
